import React, { useState } from "react"
import { useSelector, useDispatch } from "react-redux"
import { Row, Col } from "react-bootstrap"

import {
    EDIT,
    LOW_DEMAND_MODE, HIGH_DEMAND_MODE, CONTINUOUS_MODE,
    NO_DANGEROUS_FAILURE,
    NO_SAFE_FAILURE,
    NO_ARCHITECTURAL_CONSTRAINTS,
    IEC_61508_1998,
    IEC_61508_2010,
    IEC_61511_2003,
    IEC_61511_2016,
    // ISA_TR84_2002,
    ISA_TR84_2022,
    VDI_VDE_2180_2019,
} from "const"
import { hasProjectAccess } from "client/utils"
import {
    PRM_SIL_SAFETY_FUNCTION,
    StoreType,
    addModal,
} from "client/store"
import {
    PageForm,
    FormGroup,
    Label,
    Input,
    CKEditor,
    CheckboxWrapper,
    RadioButton,
    DivInput,
    Empty,
    Button,
} from "client/components"

import FormatProbability from "client/pages/sil/format/probability"
import { getMaxProbability, getMaxStr } from "client/pages/sil/formulas/internal"
import Copy from "client/pages/sil/safetyFunction/copy"

const Form = () => {
    const baseRoute = useSelector((state: StoreType) => state.silSafetyFunction.baseRoute)
    const safetyFunction = useSelector((state: StoreType) => state.silSafetyFunction.safetyFunctionView)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const [showCopy, setShowCopy] = useState(false)

    const editUrl = `${baseRoute}/${EDIT}/${safetyFunction.id}`

    const editAccess = hasProjectAccess(PRM_SIL_SAFETY_FUNCTION, EDIT)

    let probabilityTitle = ""
    if (safetyFunction.mode === LOW_DEMAND_MODE) probabilityTitle = lang.targetPfd
    if (safetyFunction.mode === HIGH_DEMAND_MODE || safetyFunction.mode === CONTINUOUS_MODE) probabilityTitle = lang.targetPfh

    return (
        <PageForm>
            <Row>
                <Col xs={12}>
                    <FormGroup>
                        <Label>{lang.name}</Label>
                        <Input type="text" value={safetyFunction.name} readOnly />
                    </FormGroup>
                    <FormGroup>
                        <Label>{lang.description}</Label>
                        <CKEditor
                            data={safetyFunction.description}
                            isReadOnly
                        />
                    </FormGroup>
                    <FormGroup>
                        <Label>{lang.calculationMethod}</Label>
                        <CheckboxWrapper displayColumn>
                            <Row>
                                <Col xs={6}>
                                    <Label style={{ margin: "0px 0px 8px 15px" }}>{lang.dangerousFailures}</Label>
                                    <RadioButton
                                        checked={safetyFunction.calculationMethodDangerous === NO_DANGEROUS_FAILURE}
                                        title={lang.noDangerousFailure}
                                        readOnly
                                    />
                                    <RadioButton
                                        checked={safetyFunction.calculationMethodDangerous === IEC_61508_2010}
                                        title={lang.iec_61508_2010}
                                        readOnly
                                    />
                                    <RadioButton
                                        checked={safetyFunction.calculationMethodDangerous === IEC_61508_1998}
                                        title={lang.iec_61508_1998}
                                        readOnly
                                    />
                                    <RadioButton
                                        checked={safetyFunction.calculationMethodDangerous === VDI_VDE_2180_2019}
                                        title={lang.vdi_vde_2180_2019}
                                        readOnly
                                    />
                                    {
                                        /*
                                        // TODO: temporarily disabled
                                        <RadioButton
                                            checked={safetyFunction.calculationMethodDangerous === ISA_TR84_2002}
                                            title={lang.isa_tr84_2002}
                                            readOnly
                                        />
                                        */
                                    }
                                    <RadioButton
                                        checked={safetyFunction.calculationMethodDangerous === ISA_TR84_2022}
                                        title={lang.isa_tr84_2022}
                                        readOnly
                                    />
                                </Col>
                                <Col xs={6}>
                                    <Label style={{ margin: "0px 0px 8px 15px" }}>{lang.safeFailures}</Label>
                                    <RadioButton
                                        checked={safetyFunction.calculationMethodSafe === NO_SAFE_FAILURE}
                                        title={lang.noSafeFailure}
                                        readOnly
                                    />
                                    <RadioButton
                                        checked={safetyFunction.calculationMethodSafe === ISA_TR84_2022}
                                        title={lang.isa_tr84_2022}
                                        readOnly
                                    />
                                </Col>
                            </Row>
                        </CheckboxWrapper>
                    </FormGroup>
                    {
                        safetyFunction.calculationMethodDangerous !== NO_DANGEROUS_FAILURE && (
                            // display only for dangerous method
                            <FormGroup>
                                <Label>{lang.architecturalConstraints}</Label>
                                <CheckboxWrapper displayColumn>
                                    <RadioButton
                                        checked={safetyFunction.architecturalConstraints === NO_ARCHITECTURAL_CONSTRAINTS}
                                        title={lang.noArchitecturalConstraints}
                                        readOnly
                                    />
                                    <RadioButton
                                        checked={safetyFunction.architecturalConstraints === IEC_61508_2010}
                                        title={lang.iec_61508_2010}
                                        readOnly
                                    />
                                    <RadioButton
                                        checked={safetyFunction.architecturalConstraints === IEC_61508_1998}
                                        title={lang.iec_61508_1998}
                                        readOnly
                                    />
                                    <RadioButton
                                        checked={safetyFunction.architecturalConstraints === IEC_61511_2016}
                                        title={lang.iec_61511_2016}
                                        readOnly
                                    />
                                    <RadioButton
                                        checked={safetyFunction.architecturalConstraints === IEC_61511_2003}
                                        title={lang.iec_61511_2003}
                                        readOnly
                                    />
                                </CheckboxWrapper>
                            </FormGroup>
                        )
                    }
                    <FormGroup>
                        <Label>{lang.mode}</Label>
                        <CheckboxWrapper>
                            <RadioButton
                                checked={safetyFunction.mode === LOW_DEMAND_MODE}
                                title={lang.modeLowDemand}
                                readOnly
                            />
                            <RadioButton
                                checked={safetyFunction.mode === HIGH_DEMAND_MODE}
                                title={lang.modeHighDemand}
                                readOnly
                            />
                            <RadioButton
                                checked={safetyFunction.mode === CONTINUOUS_MODE}
                                title={lang.modeContinuous}
                                readOnly
                            />
                        </CheckboxWrapper>
                    </FormGroup>

                    {
                        safetyFunction.calculationMethodDangerous !== NO_DANGEROUS_FAILURE && ([
                            <FormGroup key="targetSil">
                                <Label>{lang.targetSil}</Label>
                                {
                                    safetyFunction.targetSil
                                        ? <Input type="text" value={lang[`sil_${safetyFunction.targetSil}`]} readOnly />
                                        : <DivInput widePadding><Empty /></DivInput>
                                }
                            </FormGroup>,
                            <FormatProbability
                                key="targetPfd"
                                title={probabilityTitle}
                                // show "maxProbability" as default value if "targetPfd" is empty
                                data={
                                    safetyFunction.targetPfd !== null
                                        ? safetyFunction.targetPfd
                                        : getMaxProbability(safetyFunction.mode, safetyFunction.targetSil)
                                }
                                inputView
                            />,
                        ])
                    }

                    {
                        safetyFunction.calculationMethodSafe !== NO_SAFE_FAILURE && ([
                            <FormGroup key="targetStl">
                                <Label>{lang.targetStl}</Label>
                                {
                                    safetyFunction.targetStl
                                        ? <Input type="text" value={safetyFunction.targetStl} readOnly />
                                        : <DivInput widePadding><Empty /></DivInput>
                                }
                            </FormGroup>,
                            safetyFunction.targetStl !== null && (
                                <FormatProbability
                                    key="targetStr"
                                    title={lang.targetStr}
                                    // show "maxStr" as default value if "targetStr" is empty
                                    data={
                                        safetyFunction.targetStr !== null
                                            ? safetyFunction.targetStr
                                            : getMaxStr(safetyFunction.targetStl).maxStr
                                    }
                                    inputView
                                />
                            ),
                        ])
                    }

                    <FormGroup>
                        <Label>{lang.calculated}</Label>
                        <Input type="text" value={safetyFunction.calculated ? lang.yes : lang.no} readOnly />
                    </FormGroup>
                </Col>
                <Col xs={12}>
                    {
                        editAccess && (
                            <Button
                                row
                                onClick={() => dispatch(addModal({ url: editUrl }))}
                            >
                                {lang.edit}
                            </Button>
                        )
                    }
                    <Button row onClick={() => setShowCopy(true)}>{lang.copy}</Button>
                </Col>
            </Row>

            <Copy
                safetyFunction={safetyFunction}
                show={showCopy}
                onClose={() => setShowCopy(false)}
            />
        </PageForm>
    )
}
export default Form
